import { useEffect,useCallback, useMemo, useState, type KeyboardEvent } from "react";
import { getAuthFromCookie } from "../auth/auth";
interface ObjectivesScreenProps {
  onContinue: (route: string) => void;
}

const OBJECTIVES = [
  {
    title: "Unlock Smarter Spending Decisions",
    route: "/intelligence/spend",
    description:
      "Leverage the Spend module to surface hidden patterns, benchmark categories, and drive data-backed cost optimization.",
  },
  {
    title: "Streamline Cost Planning & Forecasting",
    route: "/sourcing/cost-model-workflow",
    description:
      "Use the Cost Model Workflow to build dynamic pricing models, align budgets, and accelerate approval cycles.",
  },
  {
    title: "Mitigate Risk Across Your Supply Chain",
    route: "/intelligence/supplier-risk-analysis",
    description:
      "The Third Party / Supplier Risk Management module delivers real-time risk scoring, compliance tracking, and proactive alerts.",
  },
  {
    title: "Eliminate Invoice & PO Discrepancies",
    route: "/purchase-orders/mismatch-resolution",
    description:
      "Run the Purchase Order Mismatch Query to instantly flag variances between orders, invoices, and receipts for faster resolution.",
  },
  {
    title: "Gain Full Payment Transparency",
    route: "/purchase-orders/payment-status",
    description:
      "Track every disbursement in real time with the Payment Status Query — ensuring accuracy, compliance, and vendor confidence.",
  },
];
function getGreetingByHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}
export function ObjectivesScreen({ onContinue }: ObjectivesScreenProps) {
  
  const [hour, setHour] = useState<number>(new Date().getHours());
  const user = useMemo(() => getAuthFromCookie(), []);

  useEffect(() => {
      const intervalId = window.setInterval(() => {
        setHour(new Date().getHours());
      }, 60_000);
  
      return () => {
        window.clearInterval(intervalId);
      };
    }, []);
  
    const userName =
      [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim() ||
      user?.email?.split("@")[0] ||
      "User";
  
    const greeting = `${getGreetingByHour(hour)}, ${userName}`;
  
  const [selected, setSelected] = useState<number | null>(null);

  const handleObjectiveClick = useCallback((index: number) => {
    setSelected((prev) => (prev === index ? null : index));
  }, []);

  const selectedObjective = selected === null ? null : OBJECTIVES[selected];

  const handleObjectiveKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, index: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleObjectiveClick(index);
      }
    },
    [handleObjectiveClick]
  );

  return (
    <div className="objectives-screen">
      <div className="objectives-content">
        <section className="welcome-main">
          <div className="welcome-copy card-animate-in delay-0">
            <p className="welcome-greeting">{greeting}</p>
            <h1 className="welcome-title">Welcome to ProcurAI</h1>
            <p className="welcome-subtitle">
              Turn procurement data into savings, smartest sourcing decisions, and supplier.
            </p>
          </div>
        </section>

        <header className="objectives-header card-animate-in delay-0">
          <h1 className="objectives-title">What do you want to achieve?</h1>
          <p className="objectives-subtitle">
            Select your primary objectives to personalize your experience.
          </p>
        </header>

        <section className="objectives-cards" aria-label="Objectives list">
          {OBJECTIVES.map((objective, index) => {
            const isSelected = selected === index;
            return (
              <article
                key={objective.title}
                className={`glow-card card-animate-in delay-${100 + index * 80}${isSelected ? " is-selected" : ""}`}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => handleObjectiveClick(index)}
                onKeyDown={(event) => handleObjectiveKeyDown(event, index)}>
                <div className="glow-card-content">
                  <div className="glow-card-check">
                    {isSelected ? "✓" : ""}
                  </div>
                </div>
                <h2 className="glow-card-title">{objective.title}</h2>
                <p className="glow-card-description">{objective.description}</p>
              </article>
            );
          })}
        </section>

        <div className="objectives-actions card-animate-in delay-600">
          <button
            type="button"
            onClick={() => selectedObjective && onContinue(selectedObjective.route)}
            disabled={!selectedObjective}
            className="objectives-continue-btn"
          >
            <span>Continue</span>
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
